import { Dispatch, RefObject, useReducer, useRef } from 'react';
import compact from 'lodash/compact';

import { assertUnreachable, Bean } from '@dreryk/react-common';

import { FormsTemplateInitialEditListItem, FormsTemplateListItem } from '../FormsTemplates.types';
import { EMPTY_TEMPLATE_ID } from '../list/FormsTemplatesListItemsRenderer';
import { createEmptyTemplate } from '../FormsTemplates.utils';

export type VisitFormsTemplateMode = 'READ_ONLY' | 'EDIT';

type VisitDocumentTemplateState<ITEM extends Bean> = {
   activeItem: FormsTemplateListItem<ITEM> | undefined;
   activeItemName: string | undefined;
   switchingDisabled: boolean;
   mode: VisitFormsTemplateMode | undefined;
   items: FormsTemplateListItem<ITEM>[];
};

export enum VisitDocumentTemplateActionType {
   SET_READ_ONLY_ITEM,
   SET_EDIT_ITEM,
   ENABLE_SWITCHING,
   DISABLE_SWITCHING,
   SET_ACTIVE_ITEM_NAME,
   SET_ITEMS,
   ADD_NEW_ITEM,
   REMOVE_ITEM,
}

type SetItemsAction<ITEM extends Bean> = {
   items: FormsTemplateListItem<ITEM>[];
   type: VisitDocumentTemplateActionType.SET_ITEMS;
};
type AddNewItemAction = {
   type: VisitDocumentTemplateActionType.ADD_NEW_ITEM;
};
type RemoveItemAction = {
   type: VisitDocumentTemplateActionType.REMOVE_ITEM;
   itemId: number | string;
};
type SetReadOnlyItemAction<ITEM extends Bean> = {
   type: VisitDocumentTemplateActionType.SET_READ_ONLY_ITEM;
   item: FormsTemplateListItem<ITEM> | undefined;
};
type SetEditItemAction<ITEM extends Bean> = {
   type: VisitDocumentTemplateActionType.SET_EDIT_ITEM;
   item: FormsTemplateListItem<ITEM>;
};
type EnableSwitchingAction = {
   type: VisitDocumentTemplateActionType.ENABLE_SWITCHING;
};
type DisableSwitchingAction = {
   type: VisitDocumentTemplateActionType.DISABLE_SWITCHING;
};
type SetActiveItemNameAction = {
   type: VisitDocumentTemplateActionType.SET_ACTIVE_ITEM_NAME;
   itemName: string;
};

type VisitDocumentTemplateAction<ITEM extends Bean> =
   | SetItemsAction<ITEM>
   | AddNewItemAction
   | RemoveItemAction
   | SetReadOnlyItemAction<ITEM>
   | SetEditItemAction<ITEM>
   | EnableSwitchingAction
   | DisableSwitchingAction
   | SetActiveItemNameAction;

type UseVisitDocumentTemplateReducerReturn<ITEM extends Bean> = [
   VisitDocumentTemplateState<ITEM>,
   Dispatch<VisitDocumentTemplateAction<ITEM>>
];

const handleAddNewItemAction = <ITEM extends Bean>(
   currentState: VisitDocumentTemplateState<ITEM>
): VisitDocumentTemplateState<ITEM> => {
   const emptyTemplate = createEmptyTemplate() as FormsTemplateListItem<ITEM>;
   return {
      ...currentState,
      mode: 'EDIT',
      activeItem: emptyTemplate,
      activeItemName: '',
      items: [emptyTemplate, ...currentState.items],
   };
};

const handleSetReadOnlyItemAction = <ITEM extends Bean>(
   currentState: VisitDocumentTemplateState<ITEM>,
   action: SetReadOnlyItemAction<ITEM>
): VisitDocumentTemplateState<ITEM> => {
   const nextState: VisitDocumentTemplateState<any> = {
      ...currentState,
      activeItem: action.item,
      activeItemName: action.item?.name,
      mode: action.item ? 'READ_ONLY' : undefined,
   };

   if (currentState.activeItem?.id === EMPTY_TEMPLATE_ID) {
      nextState.items = currentState.items.filter((item) => item.id !== EMPTY_TEMPLATE_ID);
   }

   return nextState;
};

const handleSetEditItemAction = <ITEM extends Bean>(
   currentState: VisitDocumentTemplateState<ITEM>,
   action: SetEditItemAction<ITEM>
): VisitDocumentTemplateState<ITEM> => {
   const nextState: VisitDocumentTemplateState<ITEM> = {
      ...currentState,
      activeItem: action.item,
      activeItemName: action.item.name,
      mode: 'EDIT',
   };

   if (currentState.activeItem?.id === EMPTY_TEMPLATE_ID) {
      nextState.items = currentState.items.filter((item) => item.id !== EMPTY_TEMPLATE_ID);
   }

   return nextState;
};

const visitDocumentTemplateListReducer = <ITEM extends Bean>(
   state: VisitDocumentTemplateState<ITEM>,
   action: VisitDocumentTemplateAction<ITEM>
): VisitDocumentTemplateState<ITEM> => {
   switch (action.type) {
      case VisitDocumentTemplateActionType.SET_READ_ONLY_ITEM:
         return handleSetReadOnlyItemAction<ITEM>(state, action);
      case VisitDocumentTemplateActionType.SET_EDIT_ITEM:
         return handleSetEditItemAction<ITEM>(state, action);
      case VisitDocumentTemplateActionType.ENABLE_SWITCHING:
         return {
            ...state,
            switchingDisabled: false,
         };
      case VisitDocumentTemplateActionType.DISABLE_SWITCHING:
         return {
            ...state,
            switchingDisabled: true,
         };
      case VisitDocumentTemplateActionType.SET_ACTIVE_ITEM_NAME:
         return {
            ...state,
            activeItemName: action.itemName,
         };
      case VisitDocumentTemplateActionType.SET_ITEMS:
         return {
            ...state,
            items: action.items,
         };
      case VisitDocumentTemplateActionType.ADD_NEW_ITEM:
         return handleAddNewItemAction<ITEM>(state);
      case VisitDocumentTemplateActionType.REMOVE_ITEM:
         return {
            ...state,
            items: state.items.filter((item) => item.id !== action.itemId),
         };
      default:
         return assertUnreachable(action);
   }
};

const getInitialEditItem = <ITEM extends Bean>(item: FormsTemplateInitialEditListItem<ITEM>): Omit<ITEM, 'id'> => {
   return item instanceof Function ? item() : item;
};

const INITIAL_STATE: VisitDocumentTemplateState<Bean> = {
   switchingDisabled: false,
   activeItem: undefined,
   activeItemName: undefined,
   mode: undefined,
   items: [],
};

export const useFormsTemplatesReducer = <ITEM extends Bean>(
   initialItems: FormsTemplateListItem<ITEM>[],
   initialEditItem?: FormsTemplateInitialEditListItem<ITEM>
): UseVisitDocumentTemplateReducerReturn<ITEM> => {
   return useReducer(
      (state: VisitDocumentTemplateState<ITEM>, action: VisitDocumentTemplateAction<ITEM>) =>
         visitDocumentTemplateListReducer<ITEM>(state, action),
      {
         ...INITIAL_STATE,
         activeItem: initialEditItem ? { ...getInitialEditItem(initialEditItem), id: EMPTY_TEMPLATE_ID } : undefined,
         activeItemName: initialEditItem ? getInitialEditItem(initialEditItem).name : '',
         mode: initialEditItem ? 'EDIT' : undefined,
         items: compact([initialEditItem, ...initialItems]),
      } as VisitDocumentTemplateState<ITEM>
   );
};

export const createSetItemsAction = (items: FormsTemplateListItem<any>[]): SetItemsAction<any> => ({
   type: VisitDocumentTemplateActionType.SET_ITEMS,
   items: items,
});
export const createAddNewItemAction = (): AddNewItemAction => ({
   type: VisitDocumentTemplateActionType.ADD_NEW_ITEM,
});
export const createRemoveItemAction = (itemId: number | string): RemoveItemAction => ({
   type: VisitDocumentTemplateActionType.REMOVE_ITEM,
   itemId: itemId,
});
export const createSetReadOnlyItemAction = (
   item: FormsTemplateListItem<any> | undefined
): SetReadOnlyItemAction<any> => ({
   type: VisitDocumentTemplateActionType.SET_READ_ONLY_ITEM,
   item: item,
});
export const createSetEditItemAction = <ITEM extends Bean>(
   item: FormsTemplateListItem<ITEM>
): SetEditItemAction<ITEM> => ({
   type: VisitDocumentTemplateActionType.SET_EDIT_ITEM,
   item: item,
});
export const createEnableSwitchingAction = (): EnableSwitchingAction => ({
   type: VisitDocumentTemplateActionType.ENABLE_SWITCHING,
});
export const createDisableSwitchingAction = (): DisableSwitchingAction => ({
   type: VisitDocumentTemplateActionType.DISABLE_SWITCHING,
});
export const createSetActiveTemplateNameAction = (itemName: string): SetActiveItemNameAction => ({
   type: VisitDocumentTemplateActionType.SET_ACTIVE_ITEM_NAME,
   itemName: itemName,
});

export type FormsTemplatesApi = {
   onActiveItemClick: VoidFunction;
};

export const useFormsTemplatesApiRef = (): RefObject<FormsTemplatesApi> => {
   return useRef<FormsTemplatesApi>(null);
};
